import { isNil } from 'lodash';
import moment from 'moment';
import { useCallback, useMemo } from 'react';
import { View } from '@shared/components/View';
import { Dropdown, DropdownButton, DropdownListItem } from '@shared/components/dropdown';
import { MBIconV2 } from '@shared/components/image';
import { MBTextBody1, MBTextBody1Bold } from '@shared/components/typography';
import { Batch } from '@shared/scenes/cards/entityBulkActions/BatchCreateContext/BatchContext';
import { Colors, grid } from '@shared/styles';
import { i18n } from '@shared/util/i18n';
import { buildCustomRRule } from './utils';

export function YearOptionsDesktop({
  rruleOptions,
  setRruleOptions,
}: Pick<Batch, 'rruleOptions' | 'setRruleOptions'>) {
  // Helper to ensure we always have a valid Date to reference
  const dtstartDate = useMemo(() => {
    if (rruleOptions?.dtstart) {
      return new Date(rruleOptions.dtstart);
    }
    return new Date();
  }, [rruleOptions]);

  const defaultMonth = dtstartDate.getUTCMonth() + 1; // RRule months are 1-indexed
  const defaultDay = dtstartDate.getUTCDate();

  /**
   * Selected months derived from rruleOptions – fallback to start month if none.
   */
  const selectedMonths = useMemo<number[]>(() => {
    if (!rruleOptions?.bymonth) {
      return [defaultMonth];
    }
    if (Array.isArray(rruleOptions.bymonth)) {
      return rruleOptions.bymonth as number[];
    }
    return [rruleOptions.bymonth as number];
  }, [rruleOptions, defaultMonth]);

  /**
   * Toggle a month in the selection.
   */
  const toggleMonth = useCallback(
    (monthNum: number) => {
      const isSelected = selectedMonths.includes(monthNum);
      let newSelection: number[];
      if (isSelected) {
        // Prevent clearing all months – must have at least one selected.
        if (selectedMonths.length === 1) {
          return;
        }
        newSelection = selectedMonths.filter(m => m !== monthNum);
      } else {
        newSelection = [...selectedMonths, monthNum];
      }
      // Sort for deterministic ordering (Jan=1 … Dec=12)
      newSelection.sort((a, b) => a - b);
      setRruleOptions(prev =>
        buildCustomRRule(
          prev,
          {
            bymonth: newSelection,
            bymonthday: !isNil(prev?.bymonthday) ? prev?.bymonthday : [defaultDay],
          },
          true,
        ),
      );
    },
    [selectedMonths, defaultDay, setRruleOptions],
  );

  /**
   * Build dropdown items – keep dropdown open after selection for multi-select.
   */
  const monthOptions = useMemo(() => {
    return moment.months().map((monthName, idx) => {
      const monthNum = idx + 1; // 1-indexed
      const isSelected = selectedMonths.includes(monthNum);
      return (
        <DropdownListItem
          key={monthName}
          onPress={() => toggleMonth(monthNum)}
          endContent={
            <div style={{ display: 'flex', flexBasis: grid(3), marginRight: -grid(1) }}>
              <MBIconV2
                color={Colors.v2.green}
                name={isSelected ? 'check-box' : 'check-box-empty'}
                size={grid(3)}
              />
            </div>
          }
        >
          {monthName}
        </DropdownListItem>
      );
    });
  }, [selectedMonths, toggleMonth]);

  const monthsDisplay = useMemo(() => {
    const names = selectedMonths.map(m =>
      moment()
        .month(m - 1)
        .format('MMMM'),
    );
    return names.join(', ');
  }, [selectedMonths]);

  return (
    <View>
      <MBTextBody1Bold>
        {i18n('workflowTemplate.component.dateAndRepeat.onDotDotDot')}
      </MBTextBody1Bold>
      <View row centerVertically paddingTop={1} paddingBottom={1} style={{ width: '100%' }}>
        <Dropdown
          button={({ onClick, isOpen }) => (
            <DropdownButton
              onClick={onClick}
              open={isOpen}
              style={{ flexGrow: 1, marginRight: grid(1), maxWidth: 'calc(100% - 50px)' }}
              showDownArrow
            >
              <View style={{ maxWidth: 'calc(100% - 20px)' }}>
                <MBTextBody1 numberOfLines={1}>{monthsDisplay}</MBTextBody1>
              </View>
            </DropdownButton>
          )}
          closeOnSelect={false}
          style={{ maxHeight: 300 }}
        >
          {monthOptions}
        </Dropdown>
        <MBTextBody1 style={{ flexShrink: 0 }}>
          {moment.localeData().ordinal(defaultDay)}
        </MBTextBody1>
      </View>
    </View>
  );
}
